// Helpers puros para a lógica de pausa de assinatura.
// Espelha 1-para-1 supabase/functions/_shared/subscriptionPause.ts.
// Regras aqui devem sempre ser mantidas em sincronia com aquele arquivo.

import type {
  StudentSubscriptionPauseRecord,
  SubscriptionPauseState,
} from '../types/subscription';

const DEFAULT_TIME_ZONE = 'America/Sao_Paulo';

function parseDateOnly(value: string | null | undefined): Date | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return null;
  }
  const parsed = new Date(`${trimmed}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDateOnly(date: Date): string {
  return date.toISOString().split('T')[0];
}

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

export function getTodayBrtDateOnly(referenceDate = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DEFAULT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(referenceDate);
}

export function addCalendarMonthsToDateOnly(dateOnly: string, months: number): string | null {
  const parsed = parseDateOnly(dateOnly);
  if (!parsed || !Number.isFinite(months)) {
    return null;
  }

  const year = parsed.getUTCFullYear();
  const month = parsed.getUTCMonth();
  const day = parsed.getUTCDate();
  const targetMonth = new Date(Date.UTC(year, month + months, 1));
  const lastDayOfTargetMonth = new Date(
    Date.UTC(targetMonth.getUTCFullYear(), targetMonth.getUTCMonth() + 1, 0),
  ).getUTCDate();

  return toDateOnly(new Date(
    Date.UTC(targetMonth.getUTCFullYear(), targetMonth.getUTCMonth(), Math.min(day, lastDayOfTargetMonth)),
  ));
}

export function getDefaultPausedUntilDate(referenceDate = new Date()): string {
  const today = getTodayBrtDateOnly(referenceDate);
  return addCalendarMonthsToDateOnly(today, 1) ?? today;
}

export function getScheduledResumeDate(pausedUntil: string): string | null {
  const until = parseDateOnly(pausedUntil);
  return until ? toDateOnly(addUtcDays(until, 1)) : null;
}

function toBrazilDateOnly(iso: string, timeZone = DEFAULT_TIME_ZONE): string | null {
  const trimmed = typeof iso === 'string' ? iso.trim() : '';
  if (!trimmed) {
    return null;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(parsed);
}

function minDate(a: string, b: string): string {
  return a <= b ? a : b;
}

export function computePauseDaysUpTo(
  pause: Pick<StudentSubscriptionPauseRecord, 'paused_from' | 'paused_until' | 'resumed_at'>,
  today: string,
  options?: { timeZone?: string },
): number {
  const from = parseDateOnly(pause.paused_from);
  const until = parseDateOnly(pause.paused_until);
  const parsedToday = parseDateOnly(today);
  if (!from || !until || !parsedToday) {
    return 0;
  }
  if (from > parsedToday) {
    return 0;
  }

  const resumedDayLocal = pause.resumed_at
    ? toBrazilDateOnly(pause.resumed_at, options?.timeZone ?? DEFAULT_TIME_ZONE)
    : null;

  let effectiveEnd: string;
  if (resumedDayLocal) {
    const parsedResumed = parseDateOnly(resumedDayLocal);
    const dayBefore = parsedResumed ? toDateOnly(addUtcDays(parsedResumed, -1)) : pause.paused_until;
    effectiveEnd = minDate(pause.paused_until, dayBefore);
  } else {
    effectiveEnd = minDate(pause.paused_until, today);
  }

  const parsedEnd = parseDateOnly(effectiveEnd);
  if (!parsedEnd || parsedEnd < from) {
    return 0;
  }

  const diffMs = parsedEnd.getTime() - from.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  return Math.max(0, days);
}

export function resolveSubscriptionPauseState(
  pauses: readonly StudentSubscriptionPauseRecord[] | null | undefined,
  today: string,
  options?: { timeZone?: string },
): SubscriptionPauseState {
  const timeZone = options?.timeZone ?? DEFAULT_TIME_ZONE;
  const list = Array.isArray(pauses) ? pauses : [];

  const totalPausedDaysUpToToday = list.reduce(
    (acc, pause) => acc + computePauseDaysUpTo(pause, today, { timeZone }),
    0,
  );

  const active =
    list
      .filter((pause) => {
        if (pause.resumed_at) {
          return false;
        }
        const from = parseDateOnly(pause.paused_from);
        const until = parseDateOnly(pause.paused_until);
        const parsedToday = parseDateOnly(today);
        if (!from || !until || !parsedToday) {
          return false;
        }
        return from <= parsedToday && parsedToday <= until;
      })
      .sort(
        (a, b) =>
          (b.paused_from ?? '').localeCompare(a.paused_from ?? '') || b.id - a.id,
      )[0] ?? null;

  const scheduledResumeDate = active
    ? getScheduledResumeDate(active.paused_until)
    : null;

  return {
    isPausedToday: Boolean(active),
    activePause: active,
    totalPausedDaysUpToToday,
    scheduledResumeDate,
  };
}

export function getEffectiveStartDate(
  startDate: string | null | undefined,
  totalPausedDaysUpToToday: number,
): string | null {
  const parsed = parseDateOnly(startDate ?? null);
  if (!parsed) {
    return null;
  }
  if (!Number.isFinite(totalPausedDaysUpToToday) || totalPausedDaysUpToToday <= 0) {
    return toDateOnly(parsed);
  }
  return toDateOnly(addUtcDays(parsed, Math.floor(totalPausedDaysUpToToday)));
}

export interface SubscriptionActivePauseRow {
  pause_id: number | null;
  paused_from: string | null;
  paused_until: string | null;
  resumed_at: string | null;
  is_active_today: boolean;
  total_paused_days_up_to_today: number;
}

export function mapActivePauseRowToState(
  row: SubscriptionActivePauseRow | null | undefined,
): SubscriptionPauseState {
  if (!row) {
    return {
      isPausedToday: false,
      activePause: null,
      totalPausedDaysUpToToday: 0,
      scheduledResumeDate: null,
    };
  }

  const activePause: StudentSubscriptionPauseRecord | null =
    row.is_active_today && row.pause_id !== null && row.paused_from && row.paused_until
      ? {
          id: row.pause_id,
          student_subscription_id: 0,
          paused_from: row.paused_from,
          paused_until: row.paused_until,
          resumed_at: row.resumed_at,
          resume_reason: null,
          reason: null,
          created_by_user_id: null,
          resumed_by_user_id: null,
          gateway_state: {},
          created_at: '',
          updated_at: '',
        }
      : null;

  const scheduledResumeDate = activePause
    ? getScheduledResumeDate(activePause.paused_until)
    : null;

  return {
    isPausedToday: row.is_active_today,
    activePause,
    totalPausedDaysUpToToday: Math.max(0, Number(row.total_paused_days_up_to_today) || 0),
    scheduledResumeDate,
  };
}

export function isEffectivelyActive(
  subscription: { canceled_at?: string | null; ended_at?: string | null } | null | undefined,
  pauseState: SubscriptionPauseState | null | undefined,
): boolean {
  if (!subscription) {
    return false;
  }
  if (subscription.canceled_at || subscription.ended_at) {
    return false;
  }
  return !pauseState?.isPausedToday;
}
